// Quick-view dialog for the composer's Step 2 creator picker: shows a
// creator's full profile (preview share, bio, linked accounts, dossier
// appearances) in a popup so admin can vet a pick without leaving the
// wizard. Data comes from GET /api/v1/creators/{handle} — an
// admin-or-owner gated read (creator profiles are NOT public; the
// preview share with real signature ink is likewise restricted to that
// audience). A creator with no handle has no profile endpoint yet, so
// the dialog falls back to the thin picker-row fields.

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@greenhold/components";

import { CreatorPreviewShare } from "./CreatorPreviewShare";
import type { ApprovedCreatorRow } from "./useAdminApprovedCreators";
import { usePublicCreatorProfile } from "./usePublicCreatorProfile";

function rowLabel(c: ApprovedCreatorRow): string {
  return c.display_name?.trim() || c.handle || c.user_id.slice(0, 8);
}

export function CreatorQuickViewDialog({
  creator,
  onClose,
}: {
  creator: ApprovedCreatorRow | null;
  onClose: () => void;
}) {
  const profileQuery = usePublicCreatorProfile(creator?.handle ?? null);

  return (
    <Dialog open={creator !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        {creator === null ? null : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-4">
                {profileQuery.data?.image_signed_url ? (
                  <img
                    src={profileQuery.data.image_signed_url}
                    alt=""
                    className="h-16 w-16 rounded-full object-cover border border-brand-forest-200"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="h-16 w-16 rounded-full bg-brand-forest-100 flex items-center justify-center text-xl font-heading text-brand-forest-700"
                  >
                    {rowLabel(creator).charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <DialogTitle>{rowLabel(creator)}</DialogTitle>
                  <DialogDescription>
                    {creator.handle ? `@${creator.handle}` : "no handle"}
                    {creator.region ? ` · ${creator.region}` : ""}
                    {creator.language ? ` · ${creator.language}` : ""}
                    {creator.creator_approved_at
                      ? ` · approved ${creator.creator_approved_at.slice(0, 10)}`
                      : ""}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {!creator.handle ? (
              <p className="text-sm opacity-70">
                This creator has no handle yet, so there's no public profile
                to show beyond the fields above.
              </p>
            ) : profileQuery.isPending ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4 rounded" />
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-2/3 rounded" />
              </div>
            ) : profileQuery.isError ? (
              <p className="text-sm text-destructive">
                Couldn't load the profile: {profileQuery.error.message}
              </p>
            ) : (
              <div className="space-y-4 text-sm">
                {/* Preview share — shared component (also used on the
                    portfolio-application detail page); React Query
                    dedupes its profile fetch with this dialog's own. */}
                <CreatorPreviewShare
                  handle={creator.handle}
                  fallbackName={rowLabel(creator)}
                />

                {profileQuery.data.bio ? (
                  <p className="whitespace-pre-wrap leading-relaxed opacity-90">
                    {profileQuery.data.bio}
                  </p>
                ) : (
                  <p className="opacity-60">No bio yet.</p>
                )}

                {profileQuery.data.linked_accounts?.length ? (
                  <div>
                    <p className="font-medium mb-1">Linked accounts</p>
                    <ul className="space-y-1" role="list">
                      {profileQuery.data.linked_accounts.map((a) => (
                        <li key={`${a.kind}-${a.url}`}>
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-dotted break-all"
                          >
                            {a.kind}: {a.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {profileQuery.data.dossiers_appearing_in.length > 0 ? (
                  <div>
                    <p className="font-medium mb-1">Appears in dossiers</p>
                    <ul className="space-y-1" role="list">
                      {profileQuery.data.dossiers_appearing_in.map((d) => (
                        <li key={d.drop_id} className="opacity-80">
                          {d.portfolio_title}{" "}
                          <span className="font-mono text-xs opacity-70">
                            ({d.drop_status})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="opacity-60">
                    Not in any dossier yet — first appearance.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
